import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ApplyPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    resumeFile: null as File | null,
    coverLetterFile: null as File | null,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.phone || !formData.position) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const form = e.currentTarget;
      const formDataToSend = new FormData(form);
      
      const response = await fetch('https://readdy.ai/api/form/d4rhjk4s30cg0huicr9g', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        navigate('/application-success');
      } else {
        alert('There was an error submitting your application. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      alert('There was an error submitting your application. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'resumeFile' | 'coverLetterFile') => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-20">
            <div className="max-w-4xl mx-auto px-6">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Join Our Team</h1>
              <p className="text-xl text-gray-300">
                Start your career with Security Bay Area
              </p>
            </div>
          </div>

          {/* Application Form */}
          <div className="max-w-4xl mx-auto px-6 py-16">
            <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Application Form</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and upload your resume. We'll review your application and get back to you soon.
              </p>

              <form 
                id="jobApplicationForm"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                    placeholder="john.smith@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Position */}
                <div>
                  <label htmlFor="position" className="block text-sm font-semibold text-gray-900 mb-2">
                    Position Applying For <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="position"
                    name="position"
                    required
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select a position</option>
                    <option value="Security Systems Installer">Security Systems Installer</option>
                    <option value="Security Technician">Security Technician</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Sales Consultant">Sales Consultant</option>
                    <option value="Customer Support Specialist">Customer Support Specialist</option>
                    <option value="Network Security Engineer">Network Security Engineer</option>
                  </select>
                </div>

                {/* Resume Upload */}
                <div>
                  <label htmlFor="resume" className="block text-sm font-semibold text-gray-900 mb-2">
                    Resume <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-coral-500 transition-colors">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      required
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'resumeFile')}
                      className="hidden"
                    />
                    <label htmlFor="resume" className="cursor-pointer">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-upload-cloud-line text-4xl text-gray-400"></i>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {formData.resumeFile ? formData.resumeFile.name : 'Click to upload resume'}
                      </p>
                      <p className="text-xs text-gray-500">PDF, DOC, or DOCX (Max 10MB)</p>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Note: File uploads will show as "Uncollectable" in the submission</p>
                </div>

                {/* Cover Letter Upload (Optional) */}
                <div>
                  <label htmlFor="coverLetter" className="block text-sm font-semibold text-gray-900 mb-2">
                    Cover Letter <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-coral-500 transition-colors">
                    <input
                      type="file"
                      id="coverLetter"
                      name="coverLetter"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'coverLetterFile')}
                      className="hidden"
                    />
                    <label htmlFor="coverLetter" className="cursor-pointer">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-file-text-line text-4xl text-gray-400"></i>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {formData.coverLetterFile ? formData.coverLetterFile.name : 'Click to upload cover letter'}
                      </p>
                      <p className="text-xs text-gray-500">PDF, DOC, or DOCX (Max 10MB)</p>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Note: File uploads will show as "Uncollectable" in the submission</p>
                </div>

                {/* Hidden field for admin email */}
                <input type="hidden" name="sendTo" value="admin@securitybayarea.com" />

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-coral-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-coral-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center text-sm text-gray-600">
              <p>
                Questions about the application process?{' '}
                <a href="/contact" className="text-coral-500 hover:text-coral-600 font-medium cursor-pointer">
                  Contact us
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
